import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, TrendingUp, TrendingDown, Minus, CheckCircle2, X } from 'lucide-react';

const verdictStyle = { higher: 'text-red-400', lower: 'text-green-400', 'in-line': 'text-white/40' };

export default function PriceCheckPanel() {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list('-created_date'),
  });
  const [results, setResults] = useState({});
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deepDive, setDeepDive] = useState(null);
  const [deepLoading, setDeepLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runScan = async () => {
    setScanning(true);
    setProgress(0);
    setResults({});
    const list = products.filter((p) => p.published !== false);
    const batch = 6;
    for (let i = 0; i < list.length; i += batch) {
      const chunk = list.slice(i, i + batch);
      try {
        const res = await base44.functions.invoke('competitorPriceCheck', { productIds: chunk.map((p) => p.id) });
        for (const r of res.data?.results || []) setResults((prev) => ({ ...prev, [r.productId]: r }));
      } catch (_e) {}
      setProgress(Math.min(100, Math.round(((i + batch) / list.length) * 100)));
    }
    setScanning(false);
  };

  const runDeepDive = async (p) => {
    setDeepDive(p);
    setDeepLoading(true);
    try {
      const res = await base44.functions.invoke('competitorPriceCheck', { productIds: [p.id] });
      const r = res.data?.results?.[0];
      setDeepDive({ ...p, analysis: r });
      if (r) setResults((prev) => ({ ...prev, [r.productId || p.id]: r }));
    } catch (_e) {}
    setDeepLoading(false);
  };

  const applyPrice = async (p, newPrice) => {
    if (!newPrice) return;
    setApplying(true);
    try {
      await base44.entities.Product.update(p.id, { price: Number(newPrice) });
      qc.invalidateQueries({ queryKey: ['products'] });
      setDeepDive(null);
    } catch (_e) {}
    setApplying(false);
  };

  const list = products.filter((p) => p.published !== false);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">COMPETITOR PRICE CHECK</p>
          <p className="text-white/60 text-sm">Scan products against UK competitor pricing. Bulk overview plus per-product deep dive.</p>
        </div>
        <button
          onClick={runScan}
          disabled={scanning}
          className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-2.5 disabled:opacity-40 flex items-center gap-2"
        >
          {scanning ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {progress}%</>
          ) : (
            <><Search className="w-4 h-4" /> RUN BULK SCAN</>
          )}
        </button>
      </div>

      <div className="border border-white/5 overflow-x-auto">
        <div className="grid grid-cols-12 px-4 py-2.5 bg-white/[0.03] text-white/30 text-[10px] font-bold tracking-widest min-w-[640px]">
          <div className="col-span-5">PRODUCT</div>
          <div className="col-span-2 text-right">YOUR PRICE</div>
          <div className="col-span-2 text-right">SUGGESTED</div>
          <div className="col-span-2 text-center">VERDICT</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {list.length === 0 && <div className="px-4 py-6 text-white/30 text-sm">No products to scan.</div>}
        {list.map((p) => {
          const r = results[p.id];
          const VerdictIcon = r?.verdict === 'higher' ? TrendingUp : r?.verdict === 'lower' ? TrendingDown : Minus;
          return (
            <div key={p.id} className="grid grid-cols-12 px-4 py-3 border-t border-white/5 items-center text-sm min-w-[640px]">
              <div className="col-span-5 text-white truncate">{p.name}</div>
              <div className="col-span-2 text-right text-white/70">£{Number(p.price).toFixed(2)}</div>
              <div className="col-span-2 text-right text-primary font-bold">
                {r ? `£${Number(r.suggestedPrice).toFixed(2)}` : '—'}
              </div>
              <div className="col-span-2 text-center">
                {r ? (
                  <span className={`inline-flex items-center gap-1 text-xs font-bold ${verdictStyle[r.verdict] || 'text-white/40'}`}>
                    <VerdictIcon className="w-3.5 h-3.5" /> {String(r.verdict).toUpperCase()}
                  </span>
                ) : (
                  <span className="text-white/20 text-xs">pending</span>
                )}
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => runDeepDive(p)} className="text-white/40 hover:text-white text-[10px] font-bold tracking-widest">
                  VIEW
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-white/30 text-[10px] mt-3">Scans use live web search (AI) — runs in batches of 6 products. Deep dive re-checks a single product for the full competitor breakdown.</p>

      {deepDive && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={() => setDeepDive(null)}>
          <div className="bg-[#111115] border border-white/10 w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-sm truncate pr-4">{deepDive.name}</h3>
              <button onClick={() => setDeepDive(null)} className="text-white/40 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </div>
            {deepLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : deepDive.analysis ? (
              <>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-white/5 p-3"><p className="text-white/30 text-[10px] tracking-widest mb-1">YOUR PRICE</p><p className="text-white font-bold">£{Number(deepDive.price).toFixed(2)}</p></div>
                  <div className="bg-white/5 p-3"><p className="text-white/30 text-[10px] tracking-widest mb-1">SUGGESTED</p><p className="text-primary font-bold">£{Number(deepDive.analysis.suggestedPrice).toFixed(2)}</p></div>
                  <div className="bg-white/5 p-3"><p className="text-white/30 text-[10px] tracking-widest mb-1">VERDICT</p><p className="text-white font-bold capitalize">{deepDive.analysis.verdict}</p></div>
                </div>
                {deepDive.analysis.competitors?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">COMPETITORS</p>
                    <div className="space-y-1.5">
                      {deepDive.analysis.competitors.map((c, i) => (
                        <div key={i} className="flex justify-between text-sm bg-white/5 px-3 py-2">
                          <span className="text-white/70 truncate pr-2">{c.retailer}</span>
                          <span className="text-white font-bold">£{Number(c.price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {deepDive.analysis.summary && <p className="text-white/60 text-xs mb-5">{deepDive.analysis.summary}</p>}
                <button
                  onClick={() => applyPrice(deepDive, deepDive.analysis.suggestedPrice)}
                  disabled={applying}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest py-3 flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} APPLY SUGGESTED PRICE
                </button>
              </>
            ) : (
              <p className="text-white/40 text-sm">No competitor data found for this product.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}